import {
  CREATE_FAILED,
  CREATE_ROOM_LABEL,
  EMPTY_ROOMS_LABEL,
  LINKS_ONCE_NOTICE,
  NO_MATCHING_ROOM_LABEL,
  SCREEN_SUBTITLE,
  SCREEN_TITLE,
  SEARCH_CLEAR,
  SEARCH_HINT,
  SEARCH_LABEL,
  SEARCH_MALFORMED,
  SEARCH_PLACEHOLDER,
  SEARCH_SUBMIT,
} from '@/components/admin/entretiens/entretien-labels';
import { RoomLinksPanel } from '@/components/admin/entretiens/room-links-panel';
import { RoomsTable } from '@/components/admin/entretiens/rooms-table';
import { Button } from '@/components/ui/button';
import { PmsSpinner } from '@/components/ui/pms-spinner';
import { useAdminSession, useRejectOn401 } from '@/lib/admin-session';
import { ApiError, createChatRoom, fetchChatRooms } from '@/lib/api';
import type { ChatRoomCredentials } from '@matter_extractor/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useId, useState } from 'react';

/**
 * S9 - the rooms (chat spec §12, issue #11).
 *
 * Behind the shell's single operator door: since BUILD-965 any account Tahiti
 * authenticates is an operator, so the person who hands out the two links needs
 * no new permission and this screen invents none.
 *
 * ## The one thing this screen must not get wrong
 *
 * The two links are answered ONCE, by the creation, and no route can serve them
 * again. So the credentials of a struck room live in component state and are
 * rendered until an operator acknowledges having copied them - and the notice
 * above the table says so BEFORE the click rather than only after it. A screen
 * that refetched the list and dropped the panel on the way would have destroyed
 * a room by refreshing.
 *
 * ## The search, and why it is a search
 *
 * §12 makes the room reference serve both sides: it is what the guest quotes to
 * ask for an erasure, so resolving that request has to be a lookup and not a
 * hunt down a list. It travels in the URL (`?reference=`) rather than in state,
 * the régime `/admin/journal` settled: a filtered screen is exactly what somebody
 * pastes into a thread, and a reload must not throw away what an operator typed.
 * NO CREDENTIAL travels there - a reference is not a secret, and the route behind
 * it is guarded.
 *
 * A malformed reference comes back 400 from the server, where `isChatReference`
 * is the guard, and this screen words the refusal: « retypez la référence »
 * rather than an empty list about a room that is right there.
 */

type EntretiensSearch = { reference?: string };

/**
 * The address bar, read as the one thing this screen filters on.
 *
 * It NEVER throws: `validateSearch` runs on whatever somebody typed, and a throw
 * there is a blank screen for a typo. The upper-casing happens here as well as
 * on the server - the alphabet is upper case by construction, and normalising at
 * the edge is what makes a link pasted in lower case work.
 */
function readSearch(search: Record<string, unknown>): EntretiensSearch {
  const raw = search.reference;
  const value = typeof raw === 'string' ? raw.trim().toUpperCase() : '';
  return value === '' ? {} : { reference: value };
}

export const Route = createFileRoute('/admin/entretiens/')({
  validateSearch: readSearch,
  component: RoomsPage,
});

function RoomsPage() {
  const { email } = useAdminSession();
  const { reference } = Route.useSearch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const searchFieldId = useId();

  /**
   * The two links of the room just struck, or nothing.
   *
   * Component state, and the ONE place in the client where a chat token lives.
   * Not in the query cache: a cache entry would be refetchable, and there is
   * nothing to refetch - the response that carried them cannot be replayed. Not
   * in `localStorage` either, for the reason §5 gives the tokens their weight:
   * they are a shared secret, and a secret that outlives the tab it was read in
   * is a secret in one more place.
   */
  const [struck, setStruck] = useState<ChatRoomCredentials | null>(null);

  /** What the search field holds while it is being typed, before it is submitted. */
  const [typed, setTyped] = useState(reference ?? '');

  const rooms = useQuery({
    // The reference is in the key, so a searched listing and the full one are
    // two cache entries and never served from each other. The email is there
    // for the reason every administration key carries it: two operators on one
    // machine must not be served each other's answers out of cache.
    queryKey: ['chat-rooms', email, reference ?? null],
    queryFn: () => fetchChatRooms(reference),
    retry: false,
  });

  const rejected = useRejectOn401(rooms.error);

  const create = useMutation({
    mutationFn: createChatRoom,
    onSuccess: (credentials) => {
      setStruck(credentials);
      // The listing gains a row - invalidated rather than written into, because
      // the row of S9 is a PROJECTION (state, duration, message count) and the
      // creation response is credentials. Assembling one from the other here
      // would be a second implementation of the reading of §12.
      void queryClient.invalidateQueries({ queryKey: ['chat-rooms', email] });
    },
  });

  /*
    A 400 is the malformed reference, and everything else is a request that
    failed. The screen holds the status, so the screen is what tells the two
    apart - the régime `/admin/journal` uses to separate « cette session n'existe
    pas » from « la requête a échoué ».
  */
  const malformed = rooms.error instanceof ApiError && rooms.error.status === 400;

  const searchFor = (next: string) =>
    navigate({
      to: '/admin/entretiens',
      search: next === '' ? {} : { reference: next.trim().toUpperCase() },
    });

  return (
    <section className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div className="space-y-1">
          <h1 className="font-bold text-3xl text-bleu-nuit">{SCREEN_TITLE}</h1>
          {/* The subtitle carries the ONE thing about this screen that is not
              visible in it: the transcripts stay after the links die (§10). */}
          <p className="max-w-[95ch] text-gray-600 text-sm">{SCREEN_SUBTITLE}</p>
        </div>
        <Button type="button" size="sm" onClick={() => create.mutate()} disabled={create.isPending}>
          {CREATE_ROOM_LABEL}
        </Button>
      </header>

      {create.isError ? <p className="text-gray-700 text-sm">{CREATE_FAILED}</p> : null}

      {struck === null ? null : (
        <RoomLinksPanel
          // Remounted per room, so a « Copié » armed on one room's link can
          // never read as copied on the next one's.
          key={struck.id}
          room={struck}
          // The origin the operator is standing on. Read here and injected, so
          // the component stays testable and no host is hardcoded - a link built
          // on the production domain from a dev stack would be a dead link
          // handed out with confidence.
          origin={window.location.origin}
          onDismiss={() => setStruck(null)}
        />
      )}

      {/* The notice sits ABOVE the table and not inside the panel alone: it has
          to be readable before the first click, or an operator learns the rule
          by losing a room to it. */}
      <div className="flex gap-3 rounded-md bg-gray-50 px-5 py-4">
        <p className="max-w-[95ch] text-gray-700 text-sm">{LINKS_ONCE_NOTICE}</p>
      </div>

      <form
        className="flex flex-wrap items-end gap-3"
        onSubmit={(event) => {
          event.preventDefault();
          void searchFor(typed);
        }}
      >
        <div className="space-y-1">
          <label
            htmlFor={searchFieldId}
            className="block font-semibold text-gray-600 text-xs uppercase tracking-wide"
          >
            {SEARCH_LABEL}
          </label>
          <input
            id={searchFieldId}
            data-testid="chat-room-search"
            value={typed}
            onChange={(event) => setTyped(event.currentTarget.value)}
            placeholder={SEARCH_PLACEHOLDER}
            // Upper case at the edge, because this string is read aloud and
            // retyped: the alphabet has no lower case, so accepting one and
            // showing another would be a field that argues with its own answer.
            className="w-52 rounded-md border border-gray-200 px-3 py-2 text-sm uppercase tabular-nums tracking-wide"
          />
        </div>
        <Button type="submit" variant="outline" size="sm">
          {SEARCH_SUBMIT}
        </Button>
        {reference === undefined ? null : (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => {
              setTyped('');
              void searchFor('');
            }}
          >
            {SEARCH_CLEAR}
          </Button>
        )}
        <p className="w-full max-w-[80ch] text-gray-500 text-xs">{SEARCH_HINT}</p>
      </form>

      {malformed ? (
        <p data-testid="chat-search-malformed" className="text-jaune-deep text-sm">
          {SEARCH_MALFORMED}
        </p>
      ) : null}

      {rooms.isPending ? (
        <div className="flex justify-center py-12">
          <PmsSpinner label="Chargement des entretiens" />
        </div>
      ) : null}

      {rooms.isError && !malformed && !rejected ? (
        <p className="text-gray-700">La liste des entretiens n’a pas pu être chargée.</p>
      ) : null}

      {rooms.data ? (
        <RoomsTable
          rooms={rooms.data.rooms}
          // Why it is empty, worded by the screen: it alone knows whether a
          // reference was searched, and « aucune salle » under an armed search
          // would send an operator to strike one she does not need.
          emptyMessage={reference === undefined ? EMPTY_ROOMS_LABEL : NO_MATCHING_ROOM_LABEL}
        />
      ) : null}
    </section>
  );
}
